import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { Plus, Ticket, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { BottomSheet, ConfirmDialog } from '@/components/ui/dialog';
import { api, type AdminPromo } from '@/lib/api';
import { haptic } from '@/lib/telegram';

export const Route = createFileRoute('/promos')({ component: AdminPromos });

const inputCls =
  'w-full rounded-xl bg-white/5 border border-white/10 px-4 h-11 text-sm outline-none focus:border-primary/60';

function AdminPromos() {
  const qc = useQueryClient();
  const { data: promos, isPending } = useQuery({ queryKey: ['admin-promos'], queryFn: () => api.promos() });
  const { data: fxRates } = useQuery({ queryKey: ['fxRates'], queryFn: () => api.fxRates() });
  const usdToRub = useMemo(() => fxRates?.find((r) => r.code === 'RUB')?.perRub ?? 90, [fxRates]);

  const [createOpen, setCreateOpen] = useState(false);
  const [toDelete, setToDelete] = useState<AdminPromo | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin-promos'] });

  const toggle = useMutation({
    mutationFn: (p: AdminPromo) => api.patchPromo(p.id, { is_active: !p.isActive }),
    onSuccess: () => {
      haptic('light');
      invalidate();
    },
    onError: () => toast.error('Не удалось изменить'),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.deletePromo(id),
    onSuccess: () => {
      haptic('success');
      toast.success('Промокод удалён');
      invalidate();
    },
    onError: () => toast.error('Нельзя удалить: есть использования. Деактивируйте.'),
  });

  const fmtDiscount = (p: AdminPromo) =>
    p.discountType === 'percent'
      ? `−${((p.discountBasisPoints ?? 0) / 100).toFixed(0)}%`
      : `−${Math.round(((p.discountFixedUsdMinor ?? 0) * usdToRub) / 100).toLocaleString('ru')} ₽`;

  return (
    <motion.div initial={false} className="p-4 space-y-4 pb-24">
      <div className="flex items-center justify-between px-2 pt-2">
        <h2 className="text-2xl font-bold tracking-tight">Промокоды</h2>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" /> Создать
        </Button>
      </div>
      <p className="px-2 text-sm text-muted-foreground">
        Скидки для клиентов. Процент или фикс. Глобальные (все боты) или на партнёра.
      </p>

      <div className="space-y-3">
        {isPending && !promos ? (
          <>
            <Skeleton className="h-[92px] rounded-2xl" />
            <Skeleton className="h-[92px] rounded-2xl" />
          </>
        ) : (promos?.length ?? 0) === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
              <Ticket className="size-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Пока нет промокодов. Создайте первый.
              </p>
            </CardContent>
          </Card>
        ) : (
          promos!.map((p) => (
            <Card key={p.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold">{p.code}</span>
                      <span className="rounded-md bg-primary/15 px-2 py-0.5 text-xs font-bold text-primary">
                        {fmtDiscount(p)}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      Использован {p.redemptionCount}
                      {p.maxUses ? ` из ${p.maxUses}` : ''} раз
                      {p.partnerId ? ' · на партнёра' : ' · глобальный'}
                    </div>
                  </div>
                  <Switch checked={p.isActive} onCheckedChange={() => toggle.mutate(p)} />
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setToDelete(p)}
                    className="flex items-center gap-1 text-xs text-red-400/80 hover:text-red-400"
                  >
                    <Trash2 className="size-3.5" /> Удалить
                  </button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <CreatePromoSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        usdToRub={usdToRub}
        onCreated={invalidate}
      />
      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => toDelete && del.mutate(toDelete.id)}
        title="Удалить промокод?"
        description={`«${toDelete?.code}» будет удалён навсегда. Если есть использования — удалить нельзя, деактивируйте.`}
        confirmLabel="Удалить"
        destructive
      />
    </motion.div>
  );
}

function CreatePromoSheet({
  open,
  onClose,
  usdToRub,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  usdToRub: number;
  onCreated: () => void;
}) {
  const [code, setCode] = useState('');
  const [type, setType] = useState<'percent' | 'fixed'>('percent');
  const [value, setValue] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [validUntil, setValidUntil] = useState('');

  const reset = () => {
    setCode('');
    setType('percent');
    setValue('');
    setMaxUses('');
    setValidUntil('');
  };

  const create = useMutation({
    mutationFn: () => {
      const v = Number(value);
      return api.createPromo({
        code: code.trim(),
        discount_type: type,
        discount_basis_points: type === 'percent' ? Math.round(v * 100) : null,
        discount_fixed_usd_minor: type === 'fixed' ? Math.round((v * 100) / usdToRub) : null,
        max_uses: maxUses ? Number(maxUses) : null,
        valid_until: validUntil ? new Date(validUntil).toISOString() : null,
        is_active: true,
      });
    },
    onSuccess: () => {
      haptic('success');
      toast.success('Промокод создан');
      reset();
      onClose();
      onCreated();
    },
    onError: () => toast.error('Не удалось создать. Проверьте код и значения.'),
  });

  const submit = () => {
    if (!code.trim()) return toast.error('Введите код');
    const v = Number(value);
    if (!v || v <= 0) return toast.error('Введите размер скидки');
    if (type === 'percent' && v > 100) return toast.error('Процент не больше 100');
    create.mutate();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Новый промокод">
      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Код
          </label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="SALE20"
            className={`${inputCls} font-mono`}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Тип скидки
          </label>
          <div className="grid grid-cols-2 gap-2">
            {(['percent', 'fixed'] as const).map((tp) => (
              <button
                key={tp}
                type="button"
                onClick={() => setType(tp)}
                className={`h-11 rounded-xl text-sm font-medium transition-colors ${
                  type === tp ? 'bg-primary text-primary-foreground' : 'bg-white/5 text-muted-foreground'
                }`}
              >
                {tp === 'percent' ? 'Процент %' : 'Фикс ₽'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {type === 'percent' ? 'Скидка, %' : 'Скидка, ₽'}
          </label>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            inputMode="numeric"
            placeholder={type === 'percent' ? '20' : '100'}
            className={inputCls}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Лимит (необяз.)
            </label>
            <input
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              inputMode="numeric"
              placeholder="∞"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              До (необяз.)
            </label>
            <input
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <Button className="w-full" onClick={submit} disabled={create.isPending}>
          {create.isPending ? 'Создаём…' : 'Создать промокод'}
        </Button>
      </div>
    </BottomSheet>
  );
}
